import React, { useCallback, useMemo, useEffect } from 'react';
import { Paper, Box, Autocomplete, TextField, Checkbox } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useTranslation } from 'react-i18next';

import MultiSelectSearchAutocomplete from '../../components/common/MultiSelectSearchAutocomplete';
import {
  searchPatients,
  searchDoctors,
  searchRadiologists,
  searchBranches,
  searchTechnicians,
  searchScans,
  searchRepresentatives,
  searchSpecializations,
  searchCategories
} from '../../services/optimizedSearchService';

const AppointmentsFilterBar = ({
  filters,
  setFilters,
  user,
  initialPatients,
  initialDoctors,
  initialRadiologists,
  scans,
  representatives,
  scanCategories,
  technicians,
  specializations,
  allowedBranches,
  canSelectBranch,
}) => {
  const { t } = useTranslation();

  // Determine user type booleans
  const isRepresentative = user?.userType === 'representative';
  const isRadiologist = user?.userType === 'radiologist';

  // ============== Memoized Initial Options ==============
  const patientOptions = useMemo(() => initialPatients.slice(0, 20), [initialPatients]);
  const doctorOptions = useMemo(() => initialDoctors.slice(0, 20), [initialDoctors]);
  const radiologistOptions = useMemo(() => initialRadiologists.slice(0, 20), [initialRadiologists]);
  const technicianOptions = useMemo(() => technicians.slice(0, 20), [technicians]);
  const scanOptions = useMemo(() => scans.slice(0, 20), [scans]);
  const branchOptions = useMemo(() => allowedBranches.slice(0, 20), [allowedBranches]);
  const representativeOptions = useMemo(() => representatives.slice(0, 20), [representatives]);
  const specializationOptions = useMemo(() => specializations.slice(0, 20), [specializations]);
  const scanCategoryOptions = useMemo(() => scanCategories.slice(0, 20), [scanCategories]);

  // ============== Label Functions ==============
  const getPatientOptionLabel = useCallback(
    (option) => `${option?.name || ''} - ${option?.phoneNumber || ''}`,
    []
  );

  const getDoctorOptionLabel = useCallback((option) => {
    const spec = option?.specialization?.name || option?.specialization || 'N/A';
    return `${option?.name || ''} - ${spec}`;
  }, []);

  const getSimpleOptionLabel = useCallback(o => o?.name || o?.username || '', []);

  // ============== Change Handlers ==============
  const handleBranchChange = useCallback((ids) => {
    setFilters(prev => ({ ...prev, branchIds: ids }));
  }, [setFilters]);

  const handlePatientChange = useCallback((ids) => {
    setFilters(prev => ({ ...prev, patientIds: ids }));
  }, [setFilters]);

  const handleDoctorChange = useCallback((ids) => {
    setFilters(prev => ({ ...prev, doctorIds: ids }));
  }, [setFilters]);

  const handleRadiologistChange = useCallback((ids) => {
    setFilters(prev => ({ ...prev, radiologistIds: ids }));
  }, [setFilters]);

  const handleTechnicianChange = useCallback((ids) => {
    setFilters(prev => ({ ...prev, technicianIds: ids }));
  }, [setFilters]);

  const handleScanChange = useCallback((ids) => {
    setFilters(prev => ({ ...prev, scanIds: ids }));
  }, [setFilters]);

  const handleRepresentativeChange = useCallback((ids) => {
    setFilters(prev => ({ ...prev, representativeIds: ids }));
  }, [setFilters]);

  const handleSpecializationChange = useCallback((ids) => {
    setFilters(prev => ({ ...prev, doctorSpecializationIds: ids }));
  }, [setFilters]);

  const handleScanCategoryChange = useCallback((ids) => {
    setFilters(prev => ({ ...prev, scanCategoryIds: ids }));
  }, [setFilters]);

  const handleFilterChange = useCallback((field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  }, [setFilters]);

  // For representatives, auto-set their ID as the filter
  useEffect(() => {
    if (isRepresentative && user?._id && !filters.representativeIds?.includes(user._id)) {
      setFilters(prev => ({
        ...prev,
        representativeIds: [user._id]
      }));
    }
  }, [isRepresentative, user?._id, setFilters, filters.representativeIds]);

  if (!user) {
    return null;
  }

  return (
    <Paper elevation={3} sx={{ mb: 3, p: 2, background: 'rgba(0,0,0,0.04)', borderRadius: 2 }}>
      {/* Main Filters Row */}
      <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">

        {/* Branch Search - visible to all, but only enabled for superAdmin and radiologist */}
        <MultiSelectSearchAutocomplete
          label={t('appointments.branch')}
          searchFunction={searchBranches}
          value={filters.branchIds || []}
          onChange={handleBranchChange}
          initialOptions={branchOptions}
          allItems={allowedBranches}
          getOptionLabel={getSimpleOptionLabel}
          minWidth={200}
          disabled={!canSelectBranch}
        />

        {/* Patient Search - hidden for radiologist */}
        {!isRadiologist && (
          <MultiSelectSearchAutocomplete
            label={t('appointments.patient')}
            searchFunction={searchPatients}
            value={filters.patientIds || []}
            onChange={handlePatientChange}
            initialOptions={patientOptions}
            allItems={initialPatients}
            getOptionLabel={getPatientOptionLabel}
            minWidth={220}
          />
        )}

        {/* Doctor Search - hidden for radiologist */}
        {!isRadiologist && (
          <MultiSelectSearchAutocomplete
            label={t('appointments.doctor')}
            searchFunction={searchDoctors}
            value={filters.doctorIds || []}
            onChange={handleDoctorChange}
            initialOptions={doctorOptions}
            allItems={initialDoctors}
            getOptionLabel={getDoctorOptionLabel}
            minWidth={220}
          />
        )}

        {/* Radiologist Search - hidden for radiologist (they can't filter by other radiologists) */}
        {!isRadiologist && (
          <MultiSelectSearchAutocomplete
            label={t('appointments.radiologist')}
            searchFunction={searchRadiologists}
            value={filters.radiologistIds || []}
            onChange={handleRadiologistChange}
            initialOptions={radiologistOptions}
            allItems={initialRadiologists}
            getOptionLabel={getSimpleOptionLabel}
            minWidth={200}
          />
        )}

        {/* Technician Search - hidden for radiologist */}
        {!isRadiologist && (
          <MultiSelectSearchAutocomplete
            label={t('radiologyTechnicians.title')}
            searchFunction={searchTechnicians}
            value={filters.technicianIds || []}
            onChange={handleTechnicianChange}
            initialOptions={technicianOptions}
            allItems={technicians}
            getOptionLabel={getSimpleOptionLabel}
            minWidth={180}
          />
        )}

        {/* Scans Search - visible to all */}
        <MultiSelectSearchAutocomplete
          label={t('navigation.scans')}
          searchFunction={searchScans}
          value={filters.scanIds || []}
          onChange={handleScanChange}
          initialOptions={scanOptions}
          allItems={scans}
          getOptionLabel={getSimpleOptionLabel}
          minWidth={200}
        />

        {/* Representative Search - hidden for radiologist */}
        {!isRadiologist && (
          <MultiSelectSearchAutocomplete
            label={t('representatives.title')}
            searchFunction={searchRepresentatives}
            value={isRepresentative ? [user._id] : (filters.representativeIds || [])}
            onChange={handleRepresentativeChange}
            initialOptions={representativeOptions}
            allItems={representatives}
            getOptionLabel={getSimpleOptionLabel}
            minWidth={180}
            disabled={isRepresentative}
          />
        )}

        {/* Doctor Specialization Search - hidden for radiologist */}
        {!isRadiologist && (
          <MultiSelectSearchAutocomplete
            label={t('doctors.specialization')}
            searchFunction={searchSpecializations}
            value={filters.doctorSpecializationIds || []}
            onChange={handleSpecializationChange}
            initialOptions={specializationOptions}
            allItems={specializations}
            getOptionLabel={getSimpleOptionLabel}
            minWidth={180}
          />
        )}

        {/* Scan Category Search - visible to all */}
        <MultiSelectSearchAutocomplete
          label={t('scanCategories.title')}
          searchFunction={searchCategories}
          value={filters.scanCategoryIds || []}
          onChange={handleScanCategoryChange}
          initialOptions={scanCategoryOptions}
          allItems={scanCategories}
          getOptionLabel={getSimpleOptionLabel}
          minWidth={180}
        />

        {/* Date Pickers - visible to all */}
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            label={t('common.startDate')}
            value={filters.startDate ? new Date(filters.startDate) : null}
            onChange={date => handleFilterChange('startDate', date)}
            slotProps={{
              textField: { sx: { width: 170 } }
            }}
          />
          <DatePicker
            label={t('common.endDate')}
            value={filters.endDate ? new Date(filters.endDate) : null}
            onChange={date => handleFilterChange('endDate', date)}
            slotProps={{
              textField: { sx: { width: 170 } }
            }}
          />
        </LocalizationProvider>
      </Box>

      {/* Status & IsPaid Filters Row */}
      <Box display="flex" gap={2} alignItems="center" mt={2} flexWrap="wrap">

        {/* Status Filter - visible to all */}
        <Autocomplete
          multiple
          options={['scheduled', 'completed', 'cancelled', 'in_progress', 'no_show']}
          getOptionLabel={option => t(`appointments.${option}`)}
          value={filters.status ? (Array.isArray(filters.status) ? filters.status : [filters.status]) : []}
          onChange={(event, newValue) => handleFilterChange('status', newValue)}
          isOptionEqualToValue={(option, value) => option === value}
          renderInput={(params) => (
            <TextField {...params} label={t('appointments.status')} sx={{ minWidth: 200 }} />
          )}
          disableCloseOnSelect
          renderOption={(props, option, { selected }) => {
            const { key, ...restProps } = props;
            return (
              <li key={option} {...restProps}>
                <Checkbox style={{ marginRight: 8 }} checked={selected} />
                {t(`appointments.${option}`)}
              </li>
            );
          }}
        />

        {/* IsPaid Filter - hidden for radiologist */}
        {!isRadiologist && (
          <Autocomplete
            options={[
              { value: true, label: t('common.paid') },
              { value: false, label: t('common.notPaid') }
            ]}
            getOptionLabel={option => option?.label || ''}
            value={
              filters.isPaid === undefined || filters.isPaid === null
                ? null
                : filters.isPaid
                  ? { value: true, label: t('common.paid') }
                  : { value: false, label: t('common.notPaid') }
            }
            onChange={(event, newValue) => {
              handleFilterChange('isPaid', newValue?.value);
            }}
            isOptionEqualToValue={(option, value) => option?.value === value?.value}
            renderInput={(params) => (
              <TextField {...params} label={t('appointments.isPaid')} sx={{ minWidth: 200 }} />
            )}
          />
        )}
      </Box>
    </Paper>
  );
};

export default AppointmentsFilterBar;